import { Button, DataTable, Stack, t, Text, TextField, Select } from '@kanbios/ui';
import type { maintenance } from '@kanbios/api-client';
import { useSession } from '../../lib/session';
import { asName } from '../../lib/environments';
import type { ProjectDraft } from './ProjectStep';

export type BatchPreview = maintenance.components['schemas']['InstallationBatchPreview'];
type Directory = BatchPreview['directories'][number];
export type BatchRow = BatchPreview['applications'][number];

/** D103: each deploy directory as the person kept it: left out or not, its name and its role. */
export function keptApplications(d: ProjectDraft, directories: Directory[]) {
  return directories
    .filter(x => !d.apps[x.deployPath]?.off)
    .map(x => ({
      deployPath: x.deployPath,
      name: x.existing ? x.name : d.apps[x.deployPath]?.name ?? x.name,
      role: x.existing ? x.role : d.apps[x.deployPath]?.role !== undefined ? d.apps[x.deployPath]!.role ?? null : x.role,
      domain: d.domains[x.deployPath] ?? '',
      values: d.apps[x.deployPath]?.values ?? {},
    }));
}

/**
 * D103 « Applications »: every deploy directory of the repository (deploy, deploy/<name>), kept by default. Each is
 * an application of the project — one already declared keeps its name and role — with what its compose exposes,
 * its contract and why it could not be installed.
 */
export function ApplicationsStep({ draft, onChange, directories, rows, errorOf, onVariables }: {
  draft: ProjectDraft;
  onChange: (d: ProjectDraft) => void;
  directories: Directory[];
  /** The preview of the kept ones (their analysis and refusals), once read. */
  rows: BatchRow[];
  errorOf: (field: string) => string | undefined;
  /** D105: opens the variables of an application. */
  onVariables: (deployPath: string) => void;
}) {
  const { enums } = useSession();
  const kept = keptApplications(draft, directories);
  const indexOf = (path: string) => kept.findIndex(k => k.deployPath === path);
  const setApp = (path: string, patch: { off?: boolean; name?: string; role?: string | null }) =>
    onChange({ ...draft, apps: { ...draft.apps, [path]: { ...draft.apps[path], ...patch } } });
  const selected = Object.fromEntries(kept.map(k => [k.deployPath, true as const]));
  const rowOf = (path: string) => rows.find(r => r.deployPath === path);
  return (
    <Stack gap={12}>
      <div id="install-applications">
        <DataTable
          label={t('install.applications.table')}
          rows={directories}
          rowKey="deployPath"
          toolbar={false}
          rowHeight="auto"
          selectable
          selected={selected}
          onSelectionChange={(sel: Record<string, true>) => onChange({
            ...draft,
            apps: Object.fromEntries(directories.map(x => [x.deployPath, { ...draft.apps[x.deployPath], off: !sel[x.deployPath] }])),
          })}
          rowState={(x: Directory) => (selected[x.deployPath] ? null : 'dimmed')}
          columns={[
            { key: 'deployPath', label: t('install.applications.columns.directory'), mono: true, pinned: true, width: 150 },
            {
              key: 'name', label: t('install.applications.columns.name'), width: 240, editable: true, sortable: false,
              render: (x: Directory) => (x.existing
                ? <Text variant="code">{x.name}</Text>
                : (
                  <TextField size="sm" mono id={`app-${indexOf(x.deployPath)}-name`} ariaLabel={t('install.applications.nameOf', { path: x.deployPath })}
                    value={draft.apps[x.deployPath]?.name ?? x.name} disabled={!selected[x.deployPath]}
                    onChange={(e: { target: { value: string } }) => setApp(x.deployPath, { name: asName(e.target.value, 50) })}
                    error={selected[x.deployPath] ? errorOf(`applications[${indexOf(x.deployPath)}].name`) : undefined} />
                )),
            },
            {
              key: 'role', label: t('install.applications.columns.role'), width: 190, editable: true, sortable: false,
              render: (x: Directory) => (x.existing
                ? <Text variant="body-sm">{enums.applicationRoles.find(r => r.value === x.role)?.label ?? x.role ?? '—'}</Text>
                : (
                  <Select size="sm" id={`app-${indexOf(x.deployPath)}-role`} ariaLabel={t('install.applications.roleOf', { path: x.deployPath })} options={enums.applicationRoles}
                    value={draft.apps[x.deployPath]?.role !== undefined ? draft.apps[x.deployPath]!.role ?? null : x.role} placeholder={t('install.applications.rolePlaceholder')}
                    disabled={!selected[x.deployPath]} onChange={(v: string | null) => setApp(x.deployPath, { role: v })}
                    error={selected[x.deployPath] ? errorOf(`applications[${indexOf(x.deployPath)}].role`) : undefined} />
                )),
            },
            {
              key: 'exposed', label: t('install.applications.columns.exposed'), width: 200, sortable: false,
              render: (x: Directory) => {
                const r = rowOf(x.deployPath);
                if (!r) return <Text variant="body-sm" tone="mute">—</Text>;
                return r.analysis.exposed.length
                  ? <Text variant="body-sm">{r.analysis.exposed.map(e => `${e.service}:${e.port}`).join(', ')}</Text>
                  : <Text variant="body-sm" tone="mute">{t('install.applications.internal')}</Text>;
              },
            },
            {
              // D105: what env.example declares, filled in before the installation.
              key: 'variables', label: t('install.applications.columns.variables'), width: 210, sortable: false,
              render: (x: Directory) => {
                const r = rowOf(x.deployPath);
                if (!selected[x.deployPath] || !r) return <Text variant="body-sm" tone="mute">—</Text>;
                const n = r.variables.items.filter(v => !v.build).length;
                if (!n) return <Text variant="body-sm" tone="mute">{t('install.applications.noVariables')}</Text>;
                return (
                  <Button id={`app-${indexOf(x.deployPath)}-variables`} size="sm" variant={r.variables.missing ? 'primary' : 'secondary'} icon={r.variables.missing ? 'edit' : 'check'}
                    onClick={() => onVariables(x.deployPath)}>
                    {r.variables.missing ? t('install.applications.variablesMissing', { n: r.variables.missing }) : t('install.applications.variablesDone', { n })}
                  </Button>
                );
              },
            },
            {
              key: 'state', label: t('install.applications.columns.state'), grow: true, width: 280, sortable: false, wrap: true,
              render: (x: Directory) => {
                const r = rowOf(x.deployPath);
                if (!selected[x.deployPath]) return <Text variant="body-sm" tone="mute">{t('install.applications.left')}</Text>;
                if (!r) return <Text variant="body-sm" tone="mute">{t('install.applications.checking')}</Text>;
                const blocking = r.refusals.filter(f => f.code !== 'domain' && f.code !== 'variables_missing');
                if (blocking.length) return <Text variant="body-sm" tone="neg">{blocking.map(f => f.message).join(' · ')}</Text>;
                const verdict = t(`deployContract.verdict.${r.analysis.verdict}`, { n: r.analysis.findings.filter(f => f.severity === 'warning').length });
                return <Text variant="body-sm">{[r.isNew === null ? null : t(r.isNew ? 'install.applications.new' : 'install.applications.existing'), verdict].filter(Boolean).join(' · ')}</Text>;
              },
            },
          ]}
        />
      </div>
      {errorOf('applications') && <Text variant="body-sm" tone="neg">{errorOf('applications')}</Text>}
    </Stack>
  );
}
